import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import PublicHeader from '../components/PublicHeader'
import { usersAPI, projectsAPI } from '../services/api'
import {
  ArrowLeft, Package, User, CheckCircle,
  CreditCard, AlertCircle, Loader2
} from 'lucide-react'

const round2 = (num) => Math.round(num * 100) / 100

const PackageCheckoutPage = () => {
  const { creatorId, packageIndex } = useParams()
  const { user } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const cancelled = searchParams.get('cancelled')

  const [creator, setCreator] = useState(null)
  const [pkg, setPkg] = useState(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [briefing, setBriefing] = useState('')

  const pkgIdx = parseInt(packageIndex)

  useEffect(() => {
    fetchCreator()
  }, [creatorId, pkgIdx])

  const fetchCreator = async () => {
    try {
      setLoading(true)
      const response = await usersAPI.getCreator(creatorId)
      const creatorData = response.data.creator || response.data
      setCreator(creatorData)

      const selectedPkg = creatorData.packages?.[pkgIdx]
      if (!selectedPkg) {
        setError('Pakket niet gevonden')
        return
      }
      setPkg(selectedPkg)
    } catch (err) {
      console.error('Error fetching creator:', err)
      setError('Creator niet gevonden')
    } finally {
      setLoading(false)
    }
  }

  const handleBookPackage = async () => {
    try {
      setSubmitting(true)
      setError('')

      const response = await projectsAPI.bookPackage({
        creatorId,
        packageIndex: pkgIdx,
        briefing: briefing.trim() || undefined
      })

      const { checkoutUrl } = response.data
      if (checkoutUrl) {
        window.location.href = checkoutUrl
      }
    } catch (err) {
      console.error('Booking error:', err)
      setError(err.response?.data?.error || 'Er is een fout opgetreden bij het boeken')
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#3EB489] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#475569]">Checkout laden...</p>
        </div>
      </div>
    )
  }

  if (error && !pkg) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-2xl font-bold mb-2">{error}</h2>
          <Link to={`/creators/${creatorId}`} className="text-[#3EB489] hover:underline">
            Terug naar creator
          </Link>
        </div>
      </div>
    )
  }

  const price = pkg.price
  // Moet gelijk blijven aan DEFAULT_DEPOSIT_PERCENT in app/backend/utils/paymentConfig.js —
  // de backend rekent het echte bedrag, dit is wat de klant vooraf ziet.
  const DEPOSIT_PERCENT = 10
  const depositExcl = round2(price * (DEPOSIT_PERCENT / 100))
  const depositBtw = round2(depositExcl * 0.21)
  const depositIncBtw = round2(depositExcl + depositBtw)

  const creatorName = creator?.firstName && creator?.lastName
    ? `${creator.firstName} ${creator.lastName}`
    : creator?.displayName || 'Creator'

  const includedLabels = {
    muziek: 'Muziek',
    script_briefing: 'Script/Briefing',
    ruwe_content: 'Ruwe content',
    video_editing: 'Video editing',
    ondertiteling: 'Ondertiteling',
    voice_over: 'Voice-over',
    productfotos: "Productfoto's",
    thumbnail: 'Thumbnail'
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#0F172A]">
      {/* Header */}
      <PublicHeader rightSlot={<span className="hidden sm:inline">Checkout</span>} />

      <div className="max-w-5xl mx-auto px-4 md:px-6 pt-6 md:pt-10 pb-20">
        {/* Back */}
        <Link to={`/creators/${creatorId}`} className="inline-flex items-center min-h-[44px] text-[#475569] hover:text-[#3EB489] transition mb-4 md:mb-8">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Terug naar {creatorName}
        </Link>

        {cancelled && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl text-amber-800 text-sm">
            Betaling geannuleerd. Je kunt het opnieuw proberen.
          </div>
        )}

        <h1 className="text-2xl font-bold mb-6 md:mb-8">Pakket boeken</h1>

        <div className="grid lg:grid-cols-5 gap-6 lg:gap-8">
          {/* Left: Creator + Package info */}
          <div className="lg:col-span-3 space-y-6">
            {/* Creator info */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-md p-6">
              <div className="flex items-center gap-4 mb-4">
                {creator?.profilePictureUrl ? (
                  <img src={creator.profilePictureUrl} alt={creatorName} className="w-14 h-14 rounded-full object-cover ring-2 ring-[#3EB489]/20" />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-[#3EB489] flex items-center justify-center ring-2 ring-[#3EB489]/20">
                    <User className="w-7 h-7 text-white" />
                  </div>
                )}
                <div>
                  <h2 className="font-bold text-lg">{creatorName}</h2>
                  {creator?.location && (
                    <p className="text-sm text-[#475569]">{creator.location.city}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Package overview */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-md p-6">
              <div className="flex items-center gap-2 mb-4">
                <Package className="w-5 h-5 text-[#3EB489]" />
                <h2 className="text-lg font-bold">{pkg.name}</h2>
              </div>

              <div className="space-y-2 text-sm mb-4">
                <div className="flex justify-between">
                  <span className="text-[#475569]">Video's</span>
                  <span className="font-medium">{pkg.videoCount || 1}x</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#475569]">Lengte</span>
                  <span className="font-medium">{pkg.videoLength || '30s'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#475569]">Levertijd</span>
                  <span className="font-medium">{pkg.deliveryDays || '6-8 dagen'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#475569]">Revisies</span>
                  <span className="font-medium">{pkg.revisions ?? 1}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#475569]">Hooks</span>
                  <span className="font-medium">{pkg.hooks ?? 1}</span>
                </div>
              </div>

              {pkg.includedItems?.length > 0 && (
                <div className="pt-4 border-t border-gray-100">
                  <p className="text-xs font-semibold text-[#475569] uppercase tracking-wider mb-2">Inclusief</p>
                  <div className="space-y-1.5">
                    {pkg.includedItems.map((item, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm text-[#0F172A]">
                        <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                        {includedLabels[item] || item}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Briefing */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-md p-6">
              <h2 className="text-lg font-bold mb-2">Briefing (optioneel)</h2>
              <p className="text-sm text-[#475569] mb-4">
                Beschrijf wat je wilt. De creator ontvangt dit na je boeking.
              </p>
              <textarea
                value={briefing}
                onChange={(e) => setBriefing(e.target.value)}
                placeholder="Beschrijf je product, doelgroep, gewenste stijl, talking points..."
                rows={5}
                maxLength={2000}
                className="w-full min-w-0 px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#3EB489]/40 focus:border-[#3EB489] resize-none text-base md:text-sm"
              />
              <p className="text-xs text-[#475569] mt-1 text-right">{briefing.length}/2000</p>
            </div>
          </div>

          {/* Right: Payment summary — op mobiel bovenaan (order-first), desktop rechts */}
          <div className="lg:col-span-2 order-first lg:order-none">
            <div className="bg-white rounded-2xl border border-gray-200 shadow-md p-5 md:p-6 lg:sticky lg:top-24">
              <h2 className="text-lg font-bold mb-4">Betalingsoverzicht</h2>

              <div className="space-y-3 text-sm mb-6">
                <div className="flex justify-between">
                  <span className="text-[#475569]">Pakketprijs excl. BTW</span>
                  <span className="font-medium">&euro;{price.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#475569]">Aanbetaling {DEPOSIT_PERCENT}%</span>
                  <span className="font-medium">&euro;{depositExcl.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#475569]">BTW 21%</span>
                  <span className="font-medium">&euro;{depositBtw.toFixed(2)}</span>
                </div>
                <div className="border-t border-gray-200 pt-3">
                  <div className="flex justify-between text-base">
                    <span className="font-bold">Nu te betalen</span>
                    <span className="font-bold text-[#3EB489]">&euro;{depositIncBtw.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {error}
                </div>
              )}

              <button
                onClick={handleBookPackage}
                disabled={submitting}
                className="w-full px-6 py-4 bg-[#3EB489] hover:bg-[#359E78] disabled:bg-[#3EB489]/50 rounded-xl font-semibold transition flex items-center justify-center gap-2 text-white text-base"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Verwerken...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-5 h-5" />
                    Betaal aanbetaling
                  </>
                )}
              </button>

              <p className="text-xs text-[#475569] mt-4 leading-relaxed">
                Restbetaling ({100 - DEPOSIT_PERCENT}% + BTW) volgt na goedkeuring van de content. Je wordt doorgestuurd naar een beveiligde betaalpagina.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PackageCheckoutPage
